/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Alert,
  Box,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import ModalBase from "./ModalBase";
import GenericForm from "../GenericForm";
import {
  VirtualBox,
  PackageConfigModalProps,
  VirtualBoxFormData,
} from "../../interfaces/packageConfig";

const validateNumericField = (value: string, fieldName: string): string => {
  const numValue = parseFloat(value);
  if (!value || isNaN(numValue) || numValue <= 0) {
    return `${fieldName} debe ser un número mayor a 0`;
  }
  return "";
};

const VirtualBoxModal: React.FC<PackageConfigModalProps> = ({
  open,
  handleClose,
  virtualBox,
  onSave,
}) => {
  const emptyFormData: VirtualBoxFormData = {
    name: "",
    maxWeight: "",
    maxWidth: "",
    maxHeight: "",
    maxDepth: "",
  };

  const [formData, setFormData] = useState<VirtualBoxFormData>({
    ...emptyFormData,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (virtualBox) {
      setFormData({
        name: virtualBox.name,
        maxWeight: virtualBox.max_weight.toString(),
        maxWidth: virtualBox.max_width.toString(),
        maxHeight: virtualBox.max_height.toString(),
        maxDepth: virtualBox.max_depth.toString(),
      });
    } else {
      setFormData(emptyFormData);
    }
    setErrors({});
  }, [virtualBox, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    const numericFields = [
      { value: formData.maxWeight, key: "maxWeight", label: "El peso" },
      { value: formData.maxWidth, key: "maxWidth", label: "El ancho" },
      { value: formData.maxHeight, key: "maxHeight", label: "La altura" },
      { value: formData.maxDepth, key: "maxDepth", label: "La profundidad" },
    ];

    numericFields.forEach(({ value, key, label }) => {
      const error = validateNumericField(value, label);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof VirtualBoxFormData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
          ...prev,
          [field]: event.target.value,
        }));

        if (errors[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: "",
          }));
        }
      };

  const calculateVolume = (): number => {
    const width = parseFloat(formData.maxWidth) || 0;
    const height = parseFloat(formData.maxHeight) || 0;
    const depth = parseFloat(formData.maxDepth) || 0;
    return width * height * depth; // Volume in cm³
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const newVirtualBox: VirtualBox = {
      _id: virtualBox?._id || `box_${Date.now()}`,
      name: formData.name.trim(),
      active: virtualBox?.active ?? true,
      max_weight: parseFloat(formData.maxWeight),
      max_volume: calculateVolume(),
      max_width: parseFloat(formData.maxWidth),
      max_height: parseFloat(formData.maxHeight),
      max_depth: parseFloat(formData.maxDepth),
    };
    onSave(newVirtualBox);
    handleClose();
  };

  const handleModalClose = () => {
    setFormData(emptyFormData);
    setErrors({});
    handleClose();
  };

  return (
    <ModalBase open={open} handleClose={handleModalClose} maxWidth="md">
      <>
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InventoryIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {virtualBox ? "Editar caja" : "Agregar nueva caja"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {virtualBox
                ? "Modificá las características de la caja seleccionada"
                : "Definí las dimensiones y características de la nueva caja"
              }
            </Typography>
          </Box>
        </Box>

        <GenericForm onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <TextField
                fullWidth
                label="Nombre de la caja"
                value={formData.name}
                onChange={handleInputChange("name")}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="ej: Caja Mediana"
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" className="m-b-16">
                Dimensiones (cm)
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Ancho"
                type="number"
                value={formData.maxWidth}
                onChange={handleInputChange("maxWidth")}
                error={!!errors.maxWidth}
                helperText={errors.maxWidth}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Altura"
                type="number"
                value={formData.maxHeight}
                onChange={handleInputChange("maxHeight")}
                error={!!errors.maxHeight}
                helperText={errors.maxHeight}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Profundidad"
                type="number"
                value={formData.maxDepth}
                onChange={handleInputChange("maxDepth")}
                error={!!errors.maxDepth}
                helperText={errors.maxDepth}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Peso máximo"
                type="number"
                value={formData.maxWeight}
                onChange={handleInputChange("maxWeight")}
                error={!!errors.maxWeight}
                helperText={
                  errors.maxWeight ||
                  "Incluí el peso del material de embalaje (papel, cinta, relleno)."
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Volumen calculado"
                disabled={true}
                value={calculateVolume().toFixed(2)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">cm³</InputAdornment>
                  ),
                }}
                helperText="Se calcula automáticamente: ancho × altura × profundidad"
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.72rem",
                    mt: 0.5,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<InfoIcon fontSize="small" />}
                sx={{ fontSize: "0.8rem", py: 0.5 }}
              >
                Esta configuración ayudará al sistema a optimizar el empaquetado
                y cotización de envíos agrupando productos de manera eficiente.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outlined"
                  onClick={handleModalClose}
                  className="m-r-10"
                >
                  Cancelar
                </Button>
                <Button variant="contained" type="submit" color="primary">
                  {virtualBox ? "Actualizar" : "Agregar"}
                </Button>
              </div>
            </Grid>
          </Grid>
        </GenericForm>
      </>
    </ModalBase>
  );
};

export default VirtualBoxModal;
